<script lang="ts">
  import { getCategories, addCategory, updateCategory, deleteCategory } from '../../lib/storage.js';
  import Notification from '../common/Notification.svelte';
  import ConfirmDialog from '../common/ConfirmDialog.svelte';
  import { createConfirmDialog } from '../../lib/confirmDialog.svelte';
  import Button from '../common/Button.svelte';
  import DeleteCategoryModal from './DeleteCategoryModal.svelte';

  const confirmDialog = createConfirmDialog();

  let { onUpdate = () => {} }: { onUpdate: () => void } = $props();

  let categories = $state<string[]>([]);
  let newCategoryName = $state('');
  let editingCategory = $state<string | null>(null);
  let editingNewName = $state('');
  let loading = $state(false);
  let error = $state<string | null>(null);
  let success = $state<string | null>(null);
  let searchTerm = $state('');
  let showDeleteModal = $state(false);
  let categoryToDelete = $state('');

  $effect(() => {
    loadCategories();
  });

  async function loadCategories() {
    categories = await getCategories();
  }

  const filteredCategories = $derived(() => {
    if (!searchTerm) return categories;
    return categories.filter(c => c.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  async function handleAddCategory() {
    if (!newCategoryName.trim()) return;

    loading = true;
    const result = await addCategory(newCategoryName.trim());

    if (result.success) {
      await loadCategories();
      newCategoryName = '';
      onUpdate();
    } else {
      error = result.error || 'Failed to add category';
    }
    loading = false;
  }

  function startEdit(category: string) {
    editingCategory = category;
    editingNewName = category;
  }

  function cancelEdit() {
    editingCategory = null;
    editingNewName = '';
  }

  async function handleUpdateCategory() {
    if (!editingNewName.trim() || editingNewName === editingCategory) {
      cancelEdit();
      return;
    }

    // Show confirmation with alias warning
    const confirmed = await confirmDialog.confirm({
      title: 'Rename Category',
      message: `Rename "${editingCategory}" to "${editingNewName.trim()}"?\n\nThis will:\n• Update all existing transactions and rules\n• Create an alias so future auto-categorization works correctly\n\nIf hardcoded categorization rules use the old name, they'll automatically map to the new name.`,
      confirmText: 'Rename',
      variant: 'primary'
    });

    if (!confirmed) return;

    loading = true;
    const result = await updateCategory(editingCategory!, editingNewName.trim());

    if (result.success) {
      await loadCategories();
      cancelEdit();
      onUpdate();

      // Show success message with update counts
      if (result.updated) {
        const parts = [];
        if (result.updated.transactions > 0) {
          parts.push(`${result.updated.transactions} transaction${result.updated.transactions !== 1 ? 's' : ''}`);
        }
        if (result.updated.rules > 0) {
          parts.push(`${result.updated.rules} rule${result.updated.rules !== 1 ? 's' : ''}`);
        }
        if (parts.length > 0) {
          success = `Category renamed. Updated ${parts.join(' and ')}. Alias created for auto-categorization.`;
        } else {
          success = 'Category renamed successfully. Alias created for auto-categorization.';
        }
      } else {
        success = 'Category renamed successfully';
      }
    } else {
      error = result.error || 'Failed to update category';
    }
    loading = false;
  }

  async function handleDeleteCategory(category: string) {
    categoryToDelete = category;
    showDeleteModal = true;
  }

  async function handleDeleteConfirm(replacementCategory: string) {
    loading = true;
    const result = await deleteCategory(categoryToDelete, replacementCategory);

    if (result.success) {
      await loadCategories();
      onUpdate();

      // Show success message with update counts
      if (result.updated) {
        const parts = [];
        if (result.updated.transactions > 0) {
          parts.push(`${result.updated.transactions} transaction${result.updated.transactions !== 1 ? 's' : ''}`);
        }
        if (result.updated.rules > 0) {
          parts.push(`${result.updated.rules} rule${result.updated.rules !== 1 ? 's' : ''}`);
        }
        if (parts.length > 0) {
          success = `Category deleted. Updated ${parts.join(' and ')} to use "${result.replacementCategory}". Alias created for auto-categorization.`;
        } else {
          success = `Category deleted successfully. Alias created for auto-categorization.`;
        }
      } else {
        success = 'Category deleted successfully';
      }
    } else {
      error = result.error || 'Failed to delete category';
    }
    loading = false;
  }

  function handleDeleteCancel() {
    categoryToDelete = '';
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      if (editingCategory) {
        handleUpdateCategory();
      } else {
        handleAddCategory();
      }
    } else if (event.key === 'Escape') {
      if (editingCategory) {
        cancelEdit();
      } else {
        newCategoryName = '';
        searchTerm = '';
      }
    }
  }
</script>

<div class="categories-tab">
  {#if error}
    <Notification type="error" message={error} onDismiss={() => error = null} />
  {/if}

  {#if success}
    <Notification type="success" message={success} autoDismiss={true} onDismiss={() => success = null} />
  {/if}

  <div class="header">
    <input
      type="text"
      bind:value={newCategoryName}
      placeholder="New category..."
      disabled={loading}
      class="input"
    />
    <Button variant="primary" size="sm" onclick={handleAddCategory} disabled={loading || !newCategoryName.trim()}>
      Add
    </Button>
    <input
      type="text"
      bind:value={searchTerm}
      placeholder="Search..."
      disabled={loading}
      class="input search"
    />
    <span class="count">{filteredCategories().length}</span>
  </div>

  <div class="grid">
    {#each filteredCategories() as category (category)}
      <div class="row">
        {#if editingCategory === category}
          <input
            type="text"
            bind:value={editingNewName}
            class="edit-input"
            disabled={loading}
          />
          <div class="actions">
            <Button variant="success" size="sm" onclick={handleUpdateCategory} disabled={loading}>Save</Button>
            <Button variant="secondary" size="sm" onclick={cancelEdit} disabled={loading}>Cancel</Button>
          </div>
        {:else}
          <span class="name">{category}</span>
          <div class="actions">
            <Button variant="primary" size="sm" onclick={() => startEdit(category)} disabled={loading}>Rename</Button>
            <Button variant="danger" size="sm" onclick={() => handleDeleteCategory(category)} disabled={loading}>Delete</Button>
          </div>
        {/if}
      </div>
    {/each}
  </div>

  {#if filteredCategories().length === 0}
    <div class="empty">
      {searchTerm ? `No match for "${searchTerm}"` : 'No categories'}
    </div>
  {/if}
</div>

<ConfirmDialog {...confirmDialog.props} />

<DeleteCategoryModal
  bind:isOpen={showDeleteModal}
  {categoryToDelete}
  availableCategories={categories}
  onConfirm={handleDeleteConfirm}
  onCancel={handleDeleteCancel}
/>

<svelte:window onkeydown={handleKeydown} />

<style lang="scss">
  .categories-tab {
    .header {
      display: flex;
      gap: var(--spacing-sm);
      margin-bottom: 8px;
      align-items: center;

      .input {
        padding: var(--spacing-sm) 9px;
        border: 1px solid var(--color-border-medium);
        border-radius: var(--radius-md);
        font-size: var(--font-size-sm);

        &:first-child {
          flex: 2;
        }

        &.search {
          flex: 1;
        }

        &:focus {
          outline: none;
          border-color: var(--color-primary-bright);
        }

        &:disabled {
          background: var(--color-background-tertiary);
        }
      }

      .count {
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-semibold);
        color: var(--color-text-muted);
        min-width: 20px;
        text-align: right;
      }
    }

    .grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--spacing-xs);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      padding: var(--spacing-xs);
      background: white;
    }

    .row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--spacing-sm);
      padding: var(--spacing-sm) 10px;
      border-radius: var(--spacing-xs);
      transition: background 0.1s;
      min-width: 0;
      background: var(--color-background-secondary);

      &:hover {
        background: var(--color-background-tertiary);
      }

      .name {
        flex: 1;
        font-size: var(--font-size-sm);
        color: var(--color-text-primary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
      }

      .edit-input {
        flex: 1;
        padding: var(--spacing-xs) 7px;
        border: 1px solid var(--color-border-medium);
        border-radius: var(--spacing-xs);
        font-size: var(--font-size-sm);
        min-width: 0;

        &:focus {
          outline: none;
          border-color: var(--color-primary-bright);
        }

        &:disabled {
          background: var(--color-background-tertiary);
        }
      }

      .actions {
        display: flex;
        gap: var(--spacing-xs);
        opacity: 0.5;
        transition: opacity 0.1s;
        flex-shrink: 0;
      }

      &:hover .actions {
        opacity: 1;
      }
    }

    .empty {
      text-align: center;
      padding: var(--spacing-2xl) 16px;
      color: var(--color-text-muted);
      font-size: var(--font-size-sm);
      background: var(--color-background-secondary);
      border-radius: var(--radius-md);
      border: 1px solid var(--color-border);
    }
  }
</style>
